import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import { Plus, Trash, PencilSimple, Users, MapPin, Phone, EnvelopeSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Clientes() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const empty = { nome: "", endereco: "", ciudad: "", nif: "", telefone: "", email: "", observacoes: "" };
  const [form, setForm] = useState(empty);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/clientes");
      setItems(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/clientes/${editing}`, form);
      } else {
        await api.post("/clientes", form);
      }
      toast.success(t("common.success"));
      setOpen(false);
      setEditing(null);
      setForm(empty);
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const openEdit = (c) => {
    setEditing(c.id);
    setForm({ nome: c.nome, endereco: c.endereco, ciudad: c.ciudad || "", nif: c.nif || "", telefone: c.telefone || "", email: c.email || "", observacoes: c.observacoes || "" });
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("?")) return;
    await api.delete(`/clientes/${id}`);
    toast.success(t("common.success"));
    load();
  };

  const filtered = items.filter((i) => i.nome.toLowerCase().includes(search.toLowerCase()) || i.endereco.toLowerCase().includes(search.toLowerCase()));

  return (
    <div data-testid="clientes-page">
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="text-3xl heading-font font-bold text-slate-900">{t("clientes.title")}</h1>
          <p className="text-slate-500 mt-1 text-sm">{t("clientes.subtitle")}</p>
        </div>

        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(empty); } }}>
          <DialogTrigger asChild>
            <Button className="bg-green-600 hover:bg-green-700 text-white gap-2" data-testid="add-cliente-btn">
              <Plus size={18} weight="bold" /> {t("clientes.add")}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editing ? t("clientes.edit") : t("clientes.add")}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>{t("clientes.name")}</Label>
                <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} required data-testid="cliente-nome-input" />
              </div>
              <div>
                <Label>{t("clientes.address")}</Label>
                <Input value={form.endereco} onChange={(e) => setForm({ ...form, endereco: e.target.value })} required data-testid="cliente-endereco-input" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>{t("clientes.ciudad")}</Label>
                  <Input value={form.ciudad} onChange={(e) => setForm({ ...form, ciudad: e.target.value })} data-testid="cliente-ciudad-input" />
                </div>
                <div>
                  <Label>{t("clientes.nif")}</Label>
                  <Input value={form.nif} onChange={(e) => setForm({ ...form, nif: e.target.value })} placeholder="Y 0000000 X" data-testid="cliente-nif-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t("clientes.phone")}</Label>
                  <Input value={form.telefone} onChange={(e) => setForm({ ...form, telefone: e.target.value })} data-testid="cliente-telefone-input" />
                </div>
                <div>
                  <Label>{t("clientes.email")}</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="cliente-email-input" />
                </div>
              </div>
              <div>
                <Label>{t("clientes.notes")}</Label>
                <Textarea value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} rows={3} />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>{t("clientes.cancel")}</Button>
                <Button type="submit" className="bg-green-600 hover:bg-green-700" data-testid="cliente-save-btn">{t("clientes.save")}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mb-5">
        <Input placeholder={t("common.search")} value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-md" data-testid="cliente-search" />
      </div>

      {loading ? (
        <Card className="p-10 text-center text-slate-400">{t("common.loading")}</Card>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center text-slate-400 border-slate-200">
          <Users size={40} weight="duotone" className="mx-auto mb-3 text-slate-300" />
          {t("clientes.empty")}
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((c) => (
            <Card key={c.id} className="p-5 card-hover border-slate-200" data-testid={`cliente-card-${c.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <h3 className="heading-font font-semibold text-slate-900 text-lg truncate">{c.nome}</h3>
                  <div className="flex items-start gap-1.5 text-sm text-slate-600 mt-1">
                    <MapPin size={14} weight="duotone" className="mt-0.5 flex-shrink-0 text-red-600" />
                    <span className="line-clamp-2">{c.endereco}</span>
                  </div>
                </div>
              </div>
              <div className="space-y-1 text-sm text-slate-600 mb-3">
                {c.ciudad && <div className="text-xs text-slate-500">{c.ciudad}</div>}
                {c.nif && <div className="text-xs font-semibold text-slate-700">NIF: {c.nif}</div>}
                {c.telefone && <div className="flex items-center gap-1.5"><Phone size={13} weight="duotone" className="text-green-600" /> {c.telefone}</div>}
                {c.email && <div className="flex items-center gap-1.5"><EnvelopeSimple size={13} weight="duotone" className="text-green-600" /> {c.email}</div>}
              </div>
              {c.observacoes && <p className="text-xs text-slate-500 pt-2 border-t border-slate-100">{c.observacoes}</p>}
              <div className="flex gap-2 mt-4 pt-3 border-t border-slate-100">
                <Button size="sm" variant="outline" onClick={() => openEdit(c)} data-testid={`edit-cliente-${c.id}`}>
                  <PencilSimple size={14} />
                </Button>
                <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(c.id)} data-testid={`delete-cliente-${c.id}`}>
                  <Trash size={14} />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
